/**
 * What a recursive ACL change leaves on ONE path — the one semantic model the Propagate dialog's effective preview
 * (`rootResultOf`), the per-path Retry target (`aclRetryTarget`) and the fake service share (S8 review fixes C #2).
 * From NOTES «S8: live-probed facts» R5 / R6 / A9 and the fix-round-C probe rows C7–C10, which follow the HDFS ACL
 * rules the service implements:
 *   - set: the change's entries (an access-only set keeps a directory's default entries, R6 i — or removes them);
 *     files take the access part (R6 iii) or refuse default entries (`defaultEntriesOnFiles`);
 *   - modify: entries replaced by key or added; a default scope the patch creates is completed from the path's access
 *     scope BEFORE the change — the owning group from its MASK when it has one (C8); every scope the patch
 *     touches without a mask gets its mask RECALCULATED (C7 / R5 i, v — also for base entries only), an explicit mask
 *     is kept (R5 ii, iv); a scope the patch does not touch keeps its mask;
 *   - remove: the named entries dropped; the mask of every scope an entry was ACTUALLY removed from is recalculated
 *     (C9 / R5 vi) — naming an absent entry changes nothing.
 * Pure; never a request.
 */
import type { StorageAclChange, StorageAclEntry, StorageAclScope } from '../../types/azureStorage';
import { aclEntryKey as keyOf } from './azureStorageAcl';
import { computeMask, orderAclEntries } from './azureStorageAclEffective';
import type { HnsServiceFacts } from './azureStorageHnsFacts';

export type AclResultFacts = Pick<HnsServiceFacts, 'accessOnlySetKeepsDefaults' | 'defaultEntriesOnFiles' | 'recursiveModifyKeepsExplicitMask' | 'recursiveRemoveRecalculatesMask'>;
export type AclRecursiveResult = { entries: StorageAclEntry[] } | { refused: 'DefaultAclOnFile' };

const SCOPES = ['access', 'default'] as const;
const copy = (entries: readonly StorageAclEntry[]): StorageAclEntry[] => entries.map((e) => ({ ...e, permissions: { ...e.permissions } }));
const isNamed = (e: Pick<StorageAclEntry, 'type' | 'id'>): boolean => (e.type === 'user' || e.type === 'group') && !!e.id;
const touches = (entries: ReadonlyArray<{ scope: StorageAclScope }>, scope: StorageAclScope): boolean => entries.some((e) => e.scope === scope);
const DEFAULT_ON_FILE: AclRecursiveResult = { refused: 'DefaultAclOnFile' };

/**
 * The default owner / owning group / other a change leaves missing, copied as the service does (A9, C8): a modify
 * copies the path's access scope BEFORE the change (`access` = the current ACL; the owning group from its mask when it
 * has one), a set copies its own access entries (the owning group from the group entry).
 */
function completeDefaultBase(entries: StorageAclEntry[], access: readonly StorageAclEntry[], groupFromMask: boolean): StorageAclEntry[] {
  if (!touches(entries, 'default')) return entries;
  const out = [...entries];
  const find = (type: StorageAclEntry['type']) => access.find((e) => e.scope === 'access' && e.type === type && !e.id);
  for (const type of ['user', 'group', 'other'] as const) {
    if (out.some((e) => e.scope === 'default' && e.type === type && !e.id)) continue;
    const source = type === 'group' && groupFromMask ? find('mask') ?? find('group') : find(type);
    if (source) out.push({ scope: 'default', type, permissions: { ...source.permissions } });
  }
  return out;
}

/** A scope's mask recalculated (union of named entries and owning group); added when named entries have none; a scope with neither is left alone. */
function recalculateMaskOf(entries: StorageAclEntry[], scope: StorageAclScope): StorageAclEntry[] {
  const hasMask = entries.some((e) => e.scope === scope && e.type === 'mask');
  if (!hasMask && !entries.some((e) => e.scope === scope && isNamed(e))) return entries;
  const mask: StorageAclEntry = { scope, type: 'mask', permissions: computeMask(entries, scope) };
  return hasMask ? entries.map((e) => (e.scope === scope && e.type === 'mask' ? mask : e)) : [...entries, mask];
}

/** A named entry sent without its scope's mask gets the computed one (A4); a mask given stays as given. */
const withNeededMasks = (entries: StorageAclEntry[]): StorageAclEntry[] =>
  SCOPES.reduce((out, scope) => (out.some((e) => e.scope === scope && e.type === 'mask') ? out : recalculateMaskOf(out, scope)), entries);

/**
 * A `modify` patch merged onto an ACL: entries replaced by key or added, a new default scope completed (A9, C8). Masks
 * stay exactly as merged — the MCP single-path merge writes this with its masks explicit (E5); a recursive modify
 * adds the service's mask recalculation (`recursiveAclResult`).
 */
export function mergeAclPatch(current: readonly StorageAclEntry[], patch: readonly StorageAclEntry[]): StorageAclEntry[] {
  const merged = copy(current);
  for (const entry of patch) {
    const at = merged.findIndex((e) => keyOf(e) === keyOf(entry));
    if (at >= 0) merged[at] = { ...entry, permissions: { ...entry.permissions } };
    else merged.push({ ...entry, permissions: { ...entry.permissions } });
  }
  return completeDefaultBase(merged, current, true);
}

function setResult(change: Extract<StorageAclChange, { mode: 'set' }>, current: readonly StorageAclEntry[], isDirectory: boolean, facts: AclResultFacts): AclRecursiveResult {
  const access = change.entries.filter((e) => e.scope === 'access');
  const defaults = change.entries.filter((e) => e.scope === 'default');
  if (!isDirectory) return defaults.length > 0 && facts.defaultEntriesOnFiles === 'failed' ? DEFAULT_ON_FILE : { entries: withNeededMasks(copy(access)) };
  if (defaults.length > 0) return { entries: withNeededMasks(completeDefaultBase(copy([...access, ...defaults]), access, false)) };
  const kept = facts.accessOnlySetKeepsDefaults.recursive ? current.filter((e) => e.scope === 'default') : [];
  return { entries: [...withNeededMasks(copy(access)), ...copy(kept)] };
}

function modifyResult(change: Extract<StorageAclChange, { mode: 'modify' }>, current: readonly StorageAclEntry[], isDirectory: boolean, facts: AclResultFacts): AclRecursiveResult {
  if (!isDirectory && touches(change.entries, 'default') && facts.defaultEntriesOnFiles === 'failed') return DEFAULT_ON_FILE;
  const patch = isDirectory ? change.entries : change.entries.filter((e) => e.scope === 'access');
  let out = mergeAclPatch(current, patch);
  for (const scope of SCOPES) {
    if (!touches(patch, scope)) continue;
    if (facts.recursiveModifyKeepsExplicitMask && patch.some((e) => e.scope === scope && e.type === 'mask')) continue;
    out = recalculateMaskOf(out, scope);
  }
  return { entries: out };
}

function removeResult(change: Extract<StorageAclChange, { mode: 'remove' }>, current: readonly StorageAclEntry[], facts: AclResultFacts): AclRecursiveResult {
  const doomed = new Set(change.entries.map((e) => keyOf(e)));
  const removedFrom = new Set<StorageAclScope>(current.filter((e) => doomed.has(keyOf(e))).map((e) => e.scope));
  let out = copy(current.filter((e) => !doomed.has(keyOf(e))));
  if (facts.recursiveRemoveRecalculatesMask) for (const scope of removedFrom) out = recalculateMaskOf(out, scope);
  return { entries: out };
}

/** The ACL `current` ends with after `change` reached it in a recursive run, in service order — or the service's per-path refusal. */
export function recursiveAclResult(change: StorageAclChange, current: readonly StorageAclEntry[], options: { isDirectory: boolean }, facts: AclResultFacts): AclRecursiveResult {
  const result = change.mode === 'set' ? setResult(change, current, options.isDirectory, facts)
    : change.mode === 'modify' ? modifyResult(change, current, options.isDirectory, facts)
      : removeResult(change, current, facts);
  return 'entries' in result ? { entries: orderAclEntries(result.entries) } : result;
}

/**
 * The scopes a recursive `modify` changes WITHOUT stating their mask: Azure recalculates those masks on every path it
 * changes (C7), which can widen what named entries and the owning group are granted — the approval and the dialog say so.
 */
export function modifyRecalculatedMaskScopes(change: StorageAclChange, facts: Pick<HnsServiceFacts, 'recursiveModifyKeepsExplicitMask'>): StorageAclScope[] {
  if (change.mode !== 'modify') return [];
  return SCOPES.filter((scope) => touches(change.entries, scope)
    && !(facts.recursiveModifyKeepsExplicitMask && change.entries.some((e) => e.scope === scope && e.type === 'mask')));
}
